import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';

/**
 * Panier Mbiyo Market (web) — même modèle que l'app native :
 * une LIGNE = produit + options choisies + instructions (line_id distinct).
 * Un seul fournisseur à la fois ; persisté en sessionStorage (best-effort).
 */
const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const lineId = (p) => {
  const opts = (p.options || []).map(o => `${o.group_id}::${o.option_id}`).sort().join(',');
  return `${p.product_id}|${opts}|${(p.special_instructions || '').trim()}`;
};

const load = () => {
  try { return JSON.parse(sessionStorage.getItem('mk_cart')) || null; } catch (_) { return null; }
};

export function CartProvider({ children }) {
  const [cart, setCartState] = useState(load);
  const setCart = useCallback((updater) => {
    setCartState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        if (next) sessionStorage.setItem('mk_cart', JSON.stringify(next));
        else sessionStorage.removeItem('mk_cart');
      } catch (_) {}
      return next;
    });
  }, []);

  const addToCart = useCallback((supplierId, supplierName, product) => {
    const id = lineId(product);
    const optionsTotal = (product.options || []).reduce((s, o) => s + (o.price_delta || 0), 0);
    const item = { ...product, line_id: id, options_total: optionsTotal, quantity: product.quantity || 1 };
    setCart(prev => {
      if (!prev || prev.supplierId !== supplierId) {
        return { supplierId, supplierName, items: [item] };
      }
      const i = prev.items.findIndex(x => x.line_id === id);
      if (i >= 0) {
        const items = [...prev.items];
        items[i] = { ...items[i], quantity: items[i].quantity + item.quantity };
        return { ...prev, items };
      }
      return { ...prev, items: [...prev.items, item] };
    });
  }, [setCart]);

  const setQty = useCallback((id, qty) => {
    setCart(prev => {
      if (!prev) return null;
      const items = qty <= 0
        ? prev.items.filter(i => i.line_id !== id)
        : prev.items.map(i => (i.line_id === id ? { ...i, quantity: qty } : i));
      return items.length ? { ...prev, items } : null;
    });
  }, [setCart]);

  const clearCart = useCallback(() => setCart(null), [setCart]);

  // Pharmacie : panier rempli depuis un DEVIS (articles et quantités fixés —
  // le serveur exige qu'ils soient identiques au devis).
  const setCartFromQuote = useCallback((quote) => {
    setCart({
      supplierId: quote.supplier_id,
      supplierName: quote.supplier?.business_name || 'Pharmacie',
      quote_id: quote.quote_id,
      items: (quote.lines || []).map(l => ({
        product_id: l.product_id, line_id: l.product_id, name: l.name,
        price: l.unit_price || 0, promo_price: null, quantity: l.quantity,
        options: [], options_total: 0, special_instructions: '',
        is_generic: l.is_generic, generic_for: l.generic_for,
        rx_original_return: l.rx_original_return === true,
      })),
    });
  }, [setCart]);

  const value = useMemo(() => {
    const items = cart?.items || [];
    const count = items.reduce((s, i) => s + i.quantity, 0);
    // « X achetés = Y offert » — MIROIR EXACT du serveur (pas de cumul, meilleur
    // entre prix affiché et bogo sur prix de base ; options plein tarif).
    // Critique : en Mobile Money on paie AVANT la commande, le montant doit coller.
    const lineFreeUnits = (i) => {
      const b = i.bogo?.buy_qty, f = i.bogo?.free_qty;
      if (!b || !f || !i.quantity) return 0;
      return Math.floor(i.quantity / (b + f)) * f;
    };
    const lineTotal = (i) => {
      const s0 = Math.round((i.promo_price || i.price || 0) * i.quantity);
      const free = lineFreeUnits(i);
      const productPart = free > 0 ? Math.min(s0, Math.round((i.price || 0) * (i.quantity - free))) : s0;
      return productPart + (i.options_total || 0) * i.quantity;
    };
    const subtotal = items.reduce((s, i) => s + lineTotal(i), 0);
    return { cart, addToCart, setQty, clearCart, setCartFromQuote, count, subtotal, lineTotal, lineFreeUnits };
  }, [cart, addToCart, setQty, clearCart, setCartFromQuote]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}
